import { useContext, type FunctionComponent } from 'react';
import { useNavigate } from 'react-router-dom';
import { track } from '@fff-web/fff-analytics';
import { Button, ButtonLink } from '@fff-web/fff-ui-shared';
import { useUserContext } from '@fff-web/fff-utilities';
import buttonLabels from '../../../../utils/buttonLabels';
import { getSwapAmountByPlanCode } from '../../../../utils/getSwapAmountByPlanCode';
import { pages } from '../../../../utils/pages';
import useSwapMainText from '../../../commons/use-swap-main-text/useSwapMainText';
import { DynamicTextContext } from '../../../context/DynamicTextContext';
import ModalContainer from '../commons/modal-container';
import * as styles from './SwapMyBoxModal.module.css';

const SWAP_STARTED = 'Swap Started';
const SWAP_SKIPPED = 'Swap Skipped';
const SWAP_EVENT_EXPERIENCE = 'Cancel Flow';
const CLOSE_BUTTON_VALUE = 'X';

interface SwapMyBoxModalType {
  closeAction: () => void;
}

const SwapMyBoxModal: FunctionComponent<SwapMyBoxModalType> = ({ closeAction }) => {
  const { dynamicText } = useContext(DynamicTextContext);
  const { userInfo } = useUserContext();
  const navigate = useNavigate();
  const swapMainTexts = useSwapMainText();

  const handleRedirectToSkipMyBox = () => {
    dynamicText?.wooUserId &&
      track({
        trackName: SWAP_STARTED,
        properties: {
          swap_amount: getSwapAmountByPlanCode(userInfo?.user?.planCode),
          cta_name: buttonLabels.SWAP_NOW,
          experience: SWAP_EVENT_EXPERIENCE,
        },
      }).finally(() => {
        document.body.style.overflow = 'auto';
        navigate(pages.SWAP_FOR_CREDIT.path);
      });
  };

  const handleCloseAction = (ctaName: string) => {
    dynamicText?.wooUserId &&
      track({
        trackName: SWAP_SKIPPED,
        properties: {
          swap_amount: getSwapAmountByPlanCode(userInfo?.user?.planCode),
          cta_name: ctaName,
          experience: SWAP_EVENT_EXPERIENCE,
        },
      });
    closeAction();
  };

  return (
    <ModalContainer closeAction={() => handleCloseAction(CLOSE_BUTTON_VALUE)}>
      <div id="saveModal" className={styles.textContent}>
        <p className={styles.doNotCancel}>{`${dynamicText?.name}, you have options...`}</p>
        <h1 className={styles.swapYourBox}>
          Swap Your Box
          <br />
          for Credit
        </h1>
        <ul className={styles.mainText}>
          {swapMainTexts.map((t, index) => {
            const liKey = `smb-modal-li-${index}`;
            return <li key={liKey}>{t}</li>;
          })}
        </ul>
        <div className={styles.yesLearnMore}>
          <Button containerFullWidth variation="primary" onClick={handleRedirectToSkipMyBox}>
            {buttonLabels.SWAP_NOW}
          </Button>
        </div>
        <div className={styles.noThanks}>
          <ButtonLink
            type="gray"
            label={buttonLabels.NO_THANKS}
            onClick={() => handleCloseAction(buttonLabels.NO_THANKS)}
          />
        </div>
      </div>
    </ModalContainer>
  );
};

export default SwapMyBoxModal;
